import {Router, UrlTree} from '@angular/router';

import {UserType} from '../enums/users/UserType';
import {rNames} from '../r.names';
import {AuthService} from '../services/auth.service';

/** Query parameter that carries the page a guard sent the visitor away from. */
export const RETURN_URL_PARAM = 'returnUrl';

/**
 * Internal path only: '/x'. Rejects protocol-relative '//host' and '/\host' (browsers read it as '//')
 * and anything with a scheme — a returnUrl from the query must never leave the site.
 */
export function safeReturnUrl(url: unknown): string | undefined {
  // query params are untyped: a repeated key arrives as an array
  if (typeof url !== 'string' || !url.startsWith('/') || url.startsWith('//') || url.startsWith('/\\')) {
    return undefined;
  }
  return url;
}

/** Sign-in flow pages: never a place to return to after signing in (they'd show a spent code or a form again). */
const AUTH_FLOW_PAGES = [
  rNames.login, rNames.register, rNames.forgotPassword, rNames.resetPassword, rNames.confirmEmail, rNames.authExternal,
].map(page => '/' + page);

function isAuthFlowPage(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  return AUTH_FLOW_PAGES.some(page => path === page || path.startsWith(page + '/'));
}

/** Default landing page of a signed-in user: admin → /admin, everyone else → /account. */
export function homeUrlFor(userType: UserType | undefined): string {
  return '/' + (userType === UserType.Admin ? rNames.admin : rNames.account);
}

/** Login page that remembers the requested URL, so the visitor comes back to it after signing in. */
export function loginTree(router: Router, returnUrl: string): UrlTree {
  return router.createUrlTree(['/', rNames.login], {queryParams: {[RETURN_URL_PARAM]: returnUrl}});
}

/**
 * Where to go right after sign-in (login, register, OAuth):
 * the page a guard asked for (query) → the page the visitor came from (header tracks it; sign-in flow pages
 * are skipped) → home by role.
 */
export function postLoginUrl(auth: AuthService, router: Router, queryReturnUrl: string | null): string {
  const fromQuery = safeReturnUrl(queryReturnUrl);
  if (fromQuery) {
    return fromQuery;
  }
  const previous = auth.returnUrl;
  const fromPrevious = previous
    ? safeReturnUrl(typeof previous === 'string' ? previous : router.serializeUrl(previous))
    : undefined;
  return fromPrevious && !isAuthFlowPage(fromPrevious) ? fromPrevious : homeUrlFor(auth.user?.userType);
}

/**
 * Browser only. authPending() is true when storage has a token but an incomplete user record; nothing resolves it
 * asynchronously today, so this is a bounded (~2 s) grace wait kept from the old guards before sending to login.
 */
export async function waitForAuth(auth: AuthService): Promise<void> {
  for (let i = 0; i < 20 && !auth.isLogged() && auth.authPending(); i++) {
    await new Promise(resolve => setTimeout(resolve, 100));
  }
}
